import { prop, computed, type Prop, type Computed } from '@tempots/dom'
import type { TokenResolutionData } from '@rh/shared/templates/design-tokens'
import {
  PAGE_SIZES,
  DESIGN_SCHEMA_VERSION,
  type HeaderComponent,
  type PageSizeKey,
  type PageMode,
  type SavedDesign,
} from '@rh/shared/schemas/design'

export type ActiveTab = 'first' | 'subsequent'

export const WATERMARK_ID = 'watermark-singleton'

// Grid — column counts chosen so cell size divides page width evenly
// (matching the sandbox prototype's grid).
export type GridSizeKey = 'xs' | 's' | 'm' | 'l' | 'xl'
export const GRID_COLS: Record<GridSizeKey, number> = {
  xs: 80,
  s: 48,
  m: 24,
  l: 12,
  xl: 6,
}

export function gridCellSize(state: DesignEditorState): number {
  return PAGE_SIZES[state.pageSize.value].w / GRID_COLS[state.gridSize.value]
}

/**
 * Reactive editor state for one design. Mirrors the sandbox prototype's
 * approach (a single `components` array, swapped when the active tab
 * changes) but as two named props instead of a "prop + stashed other-tab
 * array" pair, which plays more naturally with @tempots/dom's reactivity.
 */
export interface DesignEditorState {
  firstPageComponents: Prop<HeaderComponent[]>
  subsequentPageComponents: Prop<HeaderComponent[]>
  activeTab: Prop<ActiveTab>
  pageSize: Prop<PageSizeKey>
  pageMode: Prop<PageMode>
  selectedId: Prop<string | null>
  hoveredId: Prop<string | null>
  gridVisible: Prop<boolean>
  gridSnap: Prop<boolean>
  gridSize: Prop<GridSizeKey>
  saveStatus: Prop<'idle' | 'saving' | 'saved' | 'error'>
  /**
   * True from the moment firstAndSubsequent mode is entered until the
   * subsequent tab is first opened: the tab's stored components are stale
   * (from a previous mode/session) and must be re-seeded from the first
   * page before use. While pending, saves also mirror the first page.
   */
  subsequentSeedPending: Prop<boolean>
  /**
   * Bumped whenever the component set is swapped wholesale (wizard
   * hand-off, undo, redo). The canvas remounts its component list on each
   * bump: the KeyedForEach reuses positional subtrees when the key set is
   * replaced outright, which can leave a text renderer holding an image
   * component (ghost elements with undefined styles). Per-component edits
   * (drag, resize, z-order) don't bump it and keep the fast diff path.
   */
  designEpoch: Prop<number>
  /**
   * Live values for {{...}} tokens — the current user's own agency +
   * recruiter data. Unselected text blocks render tokens RESOLVED
   * through this (real preview); selecting a block switches to chips
   * for editing. Populated by customize-page; empty until then (chips
   * show everywhere, which is the safe fallback).
   */
  tokenData: Prop<TokenResolutionData>
  /** The active tab's components, read-only — mutate via the props above. */
  components: Computed<HeaderComponent[]>
  selectedComponent: Computed<HeaderComponent | null>
}

export function createDesignEditorState(
  initial: SavedDesign
): DesignEditorState {
  const firstPageComponents = prop<HeaderComponent[]>(
    initial.firstPage.components
  )
  const subsequentPageComponents = prop<HeaderComponent[]>(
    initial.subsequentPages.components
  )
  const activeTab = prop<ActiveTab>('first')
  const pageSize = prop<PageSizeKey>(initial.pageSize)
  const pageMode = prop<PageMode>(initial.pageMode)
  const selectedId = prop<string | null>(null)
  const hoveredId = prop<string | null>(null)
  const gridVisible = prop(true)
  const gridSnap = prop(true)
  const gridSize = prop<GridSizeKey>('s')
  const saveStatus = prop<'idle' | 'saving' | 'saved' | 'error'>('idle')
  // A design loaded already in firstAndSubsequent mode owns its subsequent
  // page as-is; the pending flag only arms on a mode *transition*.
  const subsequentSeedPending = prop(false)

  const components = computed(
    () =>
      activeTab.value === 'first'
        ? firstPageComponents.value
        : subsequentPageComponents.value,
    [activeTab, firstPageComponents, subsequentPageComponents]
  )

  const selectedComponent = computed(
    () => components.value.find(c => c.id === selectedId.value) ?? null,
    [components, selectedId]
  )

  const designEpoch = prop(0)

  return {
    designEpoch,
    firstPageComponents,
    subsequentPageComponents,
    activeTab,
    pageSize,
    pageMode,
    selectedId,
    hoveredId,
    gridVisible,
    gridSnap,
    gridSize,
    saveStatus,
    subsequentSeedPending,
    tokenData: prop<TokenResolutionData>({}),
    components,
    selectedComponent,
  }
}

/**
 * Replaces the editor's entire contents with another design (the setup
 * wizard's hand-off). The load-time saved snapshot is untouched, so the
 * editor's dirty tracking flips on automatically. Prefer
 * `actions.replaceAll` (which wraps this) so the previous design stays one
 * Undo away.
 */
export function applyDesignToEditor(
  state: DesignEditorState,
  design: SavedDesign
): void {
  state.selectedId.set(null)
  state.activeTab.set('first')
  state.pageSize.set(design.pageSize)
  state.pageMode.set(design.pageMode)
  state.firstPageComponents.set(design.firstPage.components)
  state.subsequentPageComponents.set(design.subsequentPages.components)
  state.subsequentSeedPending.set(false)
  // Wholesale swap — remount the canvas (see designEpoch).
  state.designEpoch.set(state.designEpoch.value + 1)
}

/** The prop backing the currently active tab — mutations should go through this. */
export function activeComponentsProp(
  state: DesignEditorState
): Prop<HeaderComponent[]> {
  return state.activeTab.value === 'first'
    ? state.firstPageComponents
    : state.subsequentPageComponents
}

let idCounter = 0
export function generateComponentId(prefix: string): string {
  idCounter += 1
  return `${prefix}-${Date.now()}-${idCounter}`
}

/**
 * Serializes editor state into the persisted SavedDesign shape. The first
 * page is always authoritative; subsequentPages depends on the mode:
 * - `firstOnly`: synthesized as a single full-bleed ResumeBlock (matching
 *   the sandbox's buildSavedDesign), not whatever the second tab contains.
 * - `all` (or firstAndSubsequent with the subsequent tab never opened):
 *   mirrors the first page, so stale second-tab components from a previous
 *   mode can never leak into the saved document (matching the sandbox's
 *   getBothPageComps).
 * - `firstAndSubsequent` with the tab opened: the second tab's components.
 */
export function buildSavedDesign(state: DesignEditorState): SavedDesign {
  const { w, h } = PAGE_SIZES[state.pageSize.value]
  const subsequentComponents: HeaderComponent[] =
    state.pageMode.value === 'firstOnly'
      ? [
          {
            id: 'auto-resume-subsequent',
            type: 'resume',
            x: 0,
            y: 0,
            width: w,
            height: h,
            rotation: 0,
          },
        ]
      : state.pageMode.value === 'all' || state.subsequentSeedPending.value
        ? state.firstPageComponents.value
        : state.subsequentPageComponents.value

  return {
    version: DESIGN_SCHEMA_VERSION,
    pageSize: state.pageSize.value,
    pageMode: state.pageMode.value,
    firstPage: { components: state.firstPageComponents.value },
    subsequentPages: { components: subsequentComponents },
  }
}
